import type {
  RevenueIntelligenceChatRegistrationValue,
  RevenueIntelligenceChatScopeAction,
} from "../components/RevenueIntelligenceChatContext";
import type {
  ForecastTimePeriod,
  StateOfSalesV2FilterState,
} from "./useForecastData";

type ScopeOption = {
  label: string;
  value: string;
};

type StateOfSalesChatScopeModel = {
  filters: StateOfSalesV2FilterState;
  ownersForSelect: ScopeOption[];
  pipelinesForSelect: ScopeOption[];
  teamsForSelect: ScopeOption[];
  setPeriod: (period: ForecastTimePeriod) => void;
  setSelectedOwners: (values: string[]) => void;
  setSelectedPipelines: (values: string[]) => void;
  setSelectedTeams: (values: string[]) => void;
};

export const stateOfSalesChatPeriodOptions: Array<{
  label: string;
  value: ForecastTimePeriod;
}> = [
  { label: "Previous quarter", value: "prev_quarter" },
  { label: "This quarter", value: "this_quarter" },
  { label: "Next quarter", value: "next_quarter" },
  { label: "Next 6 months", value: "next_6_months" },
  { label: "This year", value: "this_year" },
];

export function buildStateOfSalesChatScopeActions(
  model: StateOfSalesChatScopeModel,
  resolve?: (
    filters: StateOfSalesV2FilterState,
  ) => Promise<RevenueIntelligenceChatRegistrationValue[]> | RevenueIntelligenceChatRegistrationValue[],
): RevenueIntelligenceChatScopeAction[] {
  const actions: RevenueIntelligenceChatScopeAction[] = [
    ...stateOfSalesChatPeriodOptions.map(option => ({
      actionId: `state_of_sales.period.${option.value}`,
      field: "period" as const,
      label: option.label,
      values: [option.value],
      apply: () => model.setPeriod(option.value),
    })),
    {
      actionId: "state_of_sales.pipeline.all",
      aliases: ["all pipelines", "alla pipelines"],
      field: "pipeline" as const,
      label: "All pipelines",
      values: ["all"],
      apply: () => model.setSelectedPipelines([]),
    },
    ...model.pipelinesForSelect.map(option => ({
      actionId: `state_of_sales.pipeline.${option.value}`,
      aliases: [option.label],
      field: "pipeline" as const,
      label: `Pipeline: ${option.label}`,
      values: [option.value],
      apply: () => model.setSelectedPipelines([option.value]),
    })),
    {
      actionId: "state_of_sales.team.all",
      aliases: ["all teams", "alla team"],
      field: "team" as const,
      label: "All teams",
      values: ["all"],
      apply: () => model.setSelectedTeams([]),
    },
    ...model.teamsForSelect.map(option => ({
      actionId: `state_of_sales.team.${option.value}`,
      aliases: [option.label],
      field: "team" as const,
      label: `Team: ${option.label}`,
      values: [option.value],
      apply: () => model.setSelectedTeams([option.value]),
    })),
    {
      actionId: "state_of_sales.owner.all",
      aliases: ["all sales reps", "all sellers", "alla säljare"],
      field: "owner" as const,
      label: "All Sales reps",
      values: ["all"],
      apply: () => model.setSelectedOwners([]),
    },
    ...model.ownersForSelect.map(option => ({
      actionId: `state_of_sales.owner.${option.value}`,
      aliases: [option.label],
      field: "owner" as const,
      label: `Sales rep: ${option.label}`,
      values: [option.value],
      apply: () => model.setSelectedOwners([option.value]),
    })),
  ];
  if (!resolve) return actions;
  const currentFilters = model.filters;
  const resolveActions = (requestedActions: RevenueIntelligenceChatScopeAction[]) => {
    const filters: StateOfSalesV2FilterState = {
      ...currentFilters,
      owners: [...currentFilters.owners],
      pipelines: [...currentFilters.pipelines],
      teams: [...currentFilters.teams],
    };
    for (const action of requestedActions) {
      const value = action.values?.[0];
      if (!value) continue;
      if (action.field === "period") filters.period = value as ForecastTimePeriod;
      if (action.field === "pipeline") filters.pipelines = value === "all" ? [] : [value];
      if (action.field === "team") filters.teams = value === "all" ? [] : [value];
      if (action.field === "owner") filters.owners = value === "all" ? [] : [value];
    }
    return resolve(filters);
  };
  return actions.map(action => ({ ...action, resolve: resolveActions }));
}

function selectedLabels(options: ScopeOption[], selectedValues: string[]) {
  const selected = new Set(selectedValues);
  return options.filter(option => selected.has(option.value)).map(option => option.label);
}

export function getStateOfSalesChatFiltersLabel(
  model: StateOfSalesChatScopeModel & {
    selectedOwners: string[];
    selectedPipelines: string[];
    selectedTeams: string[];
  },
) {
  const parts = [
    ["Pipeline", selectedLabels(model.pipelinesForSelect, model.selectedPipelines)],
    ["Team", selectedLabels(model.teamsForSelect, model.selectedTeams)],
    ["Sales rep", selectedLabels(model.ownersForSelect, model.selectedOwners)],
  ].flatMap(([label, values]) => (
    Array.isArray(values) && values.length ? [`${label}: ${values.join(", ")}`] : []
  ));
  return parts.length ? parts.join(" · ") : undefined;
}
